import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  CloudUpload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { settingsAPI } from '../services/api';
import { useThemeContext } from '../contexts/ThemeContext';

function CompanySettingsPage() {
  const { refreshBranding } = useThemeContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [settings, setSettings] = useState({
    // Company Info
    company_name: '',
    manager_name: '',
    company_website: '',
    company_address: '',
    company_city: '',
    company_phone: '',
    company_email: '',
    // Legal Info
    rc_number: '',
    nif_number: '',
    nis_number: '',
    ai_number: '',
    // Bank Details
    bank_name: '',
    bank_account: '',
    // Branding
    logo: null,
    favicon: null,
    // Light Mode Colors
    primary_color: '#1976d2',
    secondary_color: '#dc004e',
    light_background: '#f5f5f5',
    sidebar_bg_light: '#1e293b',
    // Dark Mode Colors
    dark_primary_color: '#90caf9',
    dark_secondary_color: '#f48fb1',
    dark_background: '#121212',
    sidebar_bg_dark: '#0f172a',
    // Default Settings
    default_dark_mode: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getCompanySettings();
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const ColorPreview = ({ color, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          backgroundColor: color,
          border: '2px solid #ddd',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {label}: {color}
      </Typography>
    </Box>
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await settingsAPI.updateCompanySettings(settings);
      setSuccess('Paramètres sauvegardés avec succès');
      
      // Refresh branding to apply new colors immediately
      await refreshBranding();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await settingsAPI.uploadLogo(formData);
      setSettings(prev => ({ ...prev, logo: response.data.logo }));
      setSuccess('Logo mis à jour');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors du téléchargement du logo');
    }
  };

  const handleFaviconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('favicon', file);

    try {
      const response = await settingsAPI.uploadFavicon(formData);
      setSettings(prev => ({ ...prev, favicon: response.data.favicon }));
      setSuccess('Favicon mis à jour');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors du téléchargement du favicon');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Paramètres de l'Entreprise
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Sauvegarder
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Left Column - Company Info */}
        <Grid item xs={12} md={8}>
          {/* Company Info */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Informations de l'Entreprise</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nom de l'entreprise"
                    value={settings.company_name || ''}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nom du Gérant"
                    value={settings.manager_name || ''}
                    onChange={(e) => handleChange('manager_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Site Web"
                    value={settings.company_website || ''}
                    onChange={(e) => handleChange('company_website', e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Address & Contact */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Adresse & Contact</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Adresse"
                    value={settings.company_address || ''}
                    onChange={(e) => handleChange('company_address', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ville"
                    value={settings.company_city || ''}
                    onChange={(e) => handleChange('company_city', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Téléphone"
                    value={settings.company_phone || ''}
                    onChange={(e) => handleChange('company_phone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={settings.company_email || ''}
                    onChange={(e) => handleChange('company_email', e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Legal Info */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Informations Légales (pour factures)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="N° RC"
                    value={settings.rc_number || ''}
                    onChange={(e) => handleChange('rc_number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="N° NIF"
                    value={settings.nif_number || ''}
                    onChange={(e) => handleChange('nif_number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="N° NIS"
                    value={settings.nis_number || ''}
                    onChange={(e) => handleChange('nis_number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="N° AI"
                    value={settings.ai_number || ''}
                    onChange={(e) => handleChange('ai_number', e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Bank Details */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Informations Bancaires</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Banque"
                    value={settings.bank_name || ''}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="RIB"
                    value={settings.bank_account || ''}
                    onChange={(e) => handleChange('bank_account', e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Light Mode Colors */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <PaletteIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Couleurs Mode Clair</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.primary_color || '#1976d2'} label="Couleur Primaire" />
                  <TextField
                    fullWidth
                    label="Couleur Primaire"
                    value={settings.primary_color || '#1976d2'}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.secondary_color || '#dc004e'} label="Couleur Secondaire" />
                  <TextField
                    fullWidth
                    label="Couleur Secondaire"
                    value={settings.secondary_color || '#dc004e'}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.light_background || '#f5f5f5'} label="Arrière-plan" />
                  <TextField
                    fullWidth
                    label="Arrière-plan"
                    value={settings.light_background || '#f5f5f5'}
                    onChange={(e) => handleChange('light_background', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.sidebar_bg_light || '#1e293b'} label="Sidebar" />
                  <TextField
                    fullWidth
                    label="Sidebar"
                    value={settings.sidebar_bg_light || '#1e293b'}
                    onChange={(e) => handleChange('sidebar_bg_light', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
              </Grid>
              
              {/* Color Combination Preview */}
              <Box sx={{ mt: 3, p: 2, border: '1px solid #ddd', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Aperçu des Couleurs</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label="Primaire"
                    sx={{
                      backgroundColor: settings.primary_color || '#1976d2',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip
                    label="Secondaire"
                    sx={{
                      backgroundColor: settings.secondary_color || '#dc004e',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip
                    label="Arrière-plan"
                    sx={{
                      backgroundColor: settings.light_background || '#f5f5f5',
                      color: '#333',
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip
                    label="Sidebar"
                    sx={{
                      backgroundColor: settings.sidebar_bg_light || '#1e293b',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Dark Mode Colors */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <PaletteIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Couleurs Mode Sombre</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.dark_primary_color || '#90caf9'} label="Couleur Primaire (sombre)" />
                  <TextField
                    fullWidth
                    label="Couleur Primaire (sombre)"
                    value={settings.dark_primary_color || '#90caf9'}
                    onChange={(e) => handleChange('dark_primary_color', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.dark_secondary_color || '#f48fb1'} label="Couleur Secondaire (sombre)" />
                  <TextField
                    fullWidth
                    label="Couleur Secondaire (sombre)"
                    value={settings.dark_secondary_color || '#f48fb1'}
                    onChange={(e) => handleChange('dark_secondary_color', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.dark_background || '#121212'} label="Arrière-plan (sombre)" />
                  <TextField
                    fullWidth
                    label="Arrière-plan (sombre)"
                    value={settings.dark_background || '#121212'}
                    onChange={(e) => handleChange('dark_background', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ColorPreview color={settings.sidebar_bg_dark || '#0f172a'} label="Sidebar (sombre)" />
                  <TextField
                    fullWidth
                    label="Sidebar (sombre)"
                    value={settings.sidebar_bg_dark || '#0f172a'}
                    onChange={(e) => handleChange('sidebar_bg_dark', e.target.value)}
                    type="color"
                    InputProps={{ sx: { height: 56 } }}
                  />
                </Grid>
              </Grid>
              
              {/* Color Combination Preview */}
              <Box sx={{ mt: 3, p: 2, border: '1px solid #ddd', borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom>Aperçu des Couleurs (Mode Sombre)</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label="Primaire"
                    sx={{
                      backgroundColor: settings.dark_primary_color || '#90caf9',
                      color: '#000',
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip
                    label="Secondaire"
                    sx={{
                      backgroundColor: settings.dark_secondary_color || '#f48fb1',
                      color: '#000',
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip
                    label="Arrière-plan"
                    sx={{
                      backgroundColor: settings.dark_background || '#121212',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  />
                  <Chip
                    label="Sidebar"
                    sx={{
                      backgroundColor: settings.sidebar_bg_dark || '#0f172a',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Default Settings */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Paramètres par Défaut</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.default_dark_mode || false}
                    onChange={(e) => handleChange('default_dark_mode', e.target.checked)}
                  />
                }
                label="Activer le mode sombre par défaut"
              />
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Right Column - Branding */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Logo</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {settings.logo ? (
                  <Box
                    component="img"
                    src={settings.logo}
                    alt="Logo"
                    sx={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                  />
                ) : (
                  <Avatar sx={{ width: 100, height: 100, bgcolor: 'grey.300' }}>
                    <BusinessIcon sx={{ fontSize: 50 }} />
                  </Avatar>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Télécharger Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Favicon</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {settings.favicon ? (
                  <Box
                    component="img"
                    src={settings.favicon}
                    alt="Favicon"
                    sx={{ width: 64, height: 64, objectFit: 'contain' }}
                  />
                ) : (
                  <Avatar sx={{ width: 64, height: 64, bgcolor: 'grey.300' }}>
                    <BusinessIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  size="small"
                >
                  Télécharger Favicon
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFaviconUpload}
                  />
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default CompanySettingsPage;
